import { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/layout/Layout';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LazyRoutes } from './routes/registry';
import { UpdateBanner } from './components/ui/UpdateBanner';
import { CookieConsent } from './components/ui/CookieConsent';
import { OfflineStrip } from './components/ui/OfflineStrip';
import { TermsOfService } from './pages/TermsOfService';
import { PrivacyPolicy } from './pages/PrivacyPolicy';

const {
  Login,
  SignUp,
  ForgotPassword,
  GuestOnboarding,
  OnboardingPage,
  Home,
  EventRoom,
  Availability,
  Interests,
  ActivityDetails,
  Profile,
  CreateEventPage,
  Events,
  JoinPage,
  PlanSummary,
  NotFound,
} = LazyRoutes;

const PageLoader = () => (
  <div
    style={{
      height: '100%',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'var(--color-text-muted)',
    }}
  >
    <div className='skeleton' style={{ width: '200px', height: '20px', borderRadius: '4px' }} />
  </div>
);

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        {/* BL-14: Non-blocking SW update banner — replaces confirm() in main.tsx */}
        <UpdateBanner />
        {/* C-14: Offline indicator */}
        <OfflineStrip />
        {/* GRC-11: PECR cookie consent banner */}
        <CookieConsent />
        <Layout>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path='/terms' element={<TermsOfService />} />
              <Route path='/privacy' element={<PrivacyPolicy />} />
              <Route path='/login' element={<Login />} />
              <Route path='/signup' element={<SignUp />} />
              <Route path='/forgot-password' element={<ForgotPassword />} />
              <Route
                path='/'
                element={
                  <ProtectedRoute>
                    <Home />
                  </ProtectedRoute>
                }
              />
              {/* UR-06: Single-page wizard — replaces the 4-route nested layout */}
              <Route
                path='/create'
                element={
                  <ProtectedRoute>
                    <CreateEventPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/room/:id'
                element={
                  <ProtectedRoute allowAnonymous>
                    <EventRoom />
                  </ProtectedRoute>
                }
              />
              <Route path='/join/:eventId' element={<GuestOnboarding />} />
              <Route path='/join' element={<JoinPage />} />
              <Route path='/plan/:id/summary' element={<PlanSummary />} /> {/* New Public Route */}
              <Route
                path='/onboarding'
                element={
                  <ProtectedRoute>
                    <OnboardingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/availability'
                element={
                  <ProtectedRoute>
                    <Availability />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/interests'
                element={
                  <ProtectedRoute>
                    <Interests />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/room/:eventId/activity/:activityId'
                element={
                  <ProtectedRoute>
                    <ActivityDetails />
                  </ProtectedRoute>
                }
              />
              {/* H-88 / M-107: Discovery disabled for v1 — routes redirect to home.
                  Re-add lazy imports in registry.ts and restore FeatureFlagGate
                  here when discoveryEnabled ships. */}
              <Route path='/discovery' element={<Navigate to='/' replace />} />
              <Route path='/discovery/:id' element={<Navigate to='/' replace />} />
              <Route
                path='/profile'
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/events'
                element={
                  <ProtectedRoute allowAnonymous>
                    <Events />
                  </ProtectedRoute>
                }
              />
              <Route
                path='*'
                element={
                  <ProtectedRoute>
                    <NotFound />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Suspense>
        </Layout>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
